<template>

    <div class="px-6 py-4 bg-minplan-beige">
        <page-title :title="$t('projects.newApartment')"></page-title>
    </div>

    <jet-form-section @submitted="createApartment()">

        <template #form>


            <!-- Card Apartment Information -->
            <form-card :title="$t('apartments.apartment')" :description="$t('apartments.apartmentDescription')" class="shadow-none">

                <!-- Durchgehendes Raster: drei Zeilen zu je zwei gleich breiten
                     Feldern (Entscheid Peter, 31.08.2026) — derselbe Rhythmus wie
                     bei Gebühr und Honorar. Vorher waren die Breiten 4/2, 3/3, 2/4;
                     das wirkte unruhig. -->
                <div class="col-span-3">
                    <label for="number" class="block text-sm font-medium text-gray-700">{{ $t('apartments.number') }}</label>
                    <input type="text" name="number" id="number" v-model="form.number" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" autofocus />
                    <jet-input-error :message="form.errors.number" class="mt-2" />
                </div>

                <div class="col-span-3">
                    <label for="floor" class="block text-sm font-medium text-gray-700">{{ $t('apartments.floor') }} <RequiredMark /></label>
                    <!-- Das Stockwerkfeld trug `id="area"` und `name="text"` — dieselbe
                         id wie die Nettowohnfläche darunter. Doppelte ids sind ungültiges
                         HTML: das Label «Stockwerk» zeigte auf das falsche Feld, und ein
                         Klick darauf sprang in die Fläche. -->
                    <input type="text" name="floor" id="floor" v-model="form.floor" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors.floor" class="mt-2" />
                </div>

                <div class="col-span-3">
                    <label for="rooms" class="block text-sm font-medium text-gray-700">{{ $t('apartments.roomsAmount') }} <RequiredMark /></label>
                    <Dropdown v-model="form.rooms"
                                id="rooms"
                            :options="rooms"
                            optionLabel="name"
                            :filter="true"
                            :auto-filter-focus="true"
                            filter-match-mode="startsWith"
                            :placeholder="$t('apartments.roomsAmount')"
                            :showClear="false"
                            class="w-full mt-1 min-w-0 sm:text-sm">
                    </Dropdown>
                    <jet-input-error :message="form.errors.rooms" class="mt-2" />
                </div>

                <div class="col-span-3">
                    <label for="area" class="block text-sm font-medium text-gray-700">{{ $t('apartments.area') }} <RequiredMark /></label>
                    <div class="flex">
                        <input type="number" step=".01" name="area" id="area" v-model="form.area" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                        <span class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1">&#13217;</span>
                    </div>
                    <jet-input-error :message="form.errors.area" class="mt-2" />
                </div>

                <!-- Parkplatz links, Gebäude rechts daneben (Entscheid Peter,
                     31.08.2026). Bewusst der Parkplatz zuerst: hat ein Projekt
                     keine Gebäude, entfällt das Feld daneben — dann steht der
                     Parkplatz an derselben Stelle wie sonst und nicht plötzlich
                     allein am rechten Rand. -->
                <div class="col-span-3">
                    <label for="parking" class="block text-sm font-medium text-gray-700">{{ $t('apartments.parking') }}</label>
                    <input type="text" name="parking" id="parking" v-model="form.parking" :placeholder="$t('apartments.parkingPlaceholder')" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors.parking" class="mt-2" />
                </div>

                <!-- Entscheid 2.4: Sobald das Projekt Gebäude hat, ist die Zuordnung
                     Pflicht. Ohne Gebäude entfällt das Feld ganz. -->
                <div v-if="project.buildings && project.buildings.length" class="col-span-3">
                    <label for="building" class="block text-sm font-medium text-gray-700">{{ $t('buildings.building') }}
                        <RequiredMark /></label>
                    <Dropdown id="building" v-model="form.building"
                              :options="project.buildings"
                              optionLabel="name"
                              optionValue="uuid"
                              :placeholder="$t('buildings.choose')"
                              :showClear="false"
                              class="w-full mt-1 min-w-0 sm:text-sm" />
                    <jet-input-error :message="form.errors.building" class="mt-2" />
                </div>


            </form-card>

            <!-- Card Apartment Settings -->
            <form-card :title="$t('apartments.settings')" :description="$t('apartments.settingsDescription')">
                <div class="col-span-3">
                    <label for="base_fee" class="block text-sm font-medium text-gray-700">{{ $t("projects.baseFee") }} <RequiredMark /></label>
                    <div class="flex">
                        <input type="number" step="0.01" name="base_fee" id="base_fee" v-model="form.base_fee" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                        <span class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1">%</span>
                    </div>       
                    <jet-input-error :message="form.errors.base_fee" class="mt-2" />
                </div>

                <div class="col-span-3">
                    <label for="consultancy_fee " class="block text-sm font-medium text-gray-700">{{ $t("projects.consultancyFee") }} <RequiredMark /></label>
                    <div class="flex">
                        <input type="number" step="0.01" name="consultancy_fee" id="consultancy_fee" v-model="form.consultancy_fee" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                        <span class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1">%</span>
                    </div> 
                    <jet-input-error :message="form.errors.consultancy_fee" class="mt-2" />
                </div> 
            </form-card>

        </template>

        <template #actions>
            <div class="flex justify-end my-4 mx-6">

                <button @click="cancel" type="button" class="inline-block uppercase font-semibold text-xs tracking-widest">{{ $t('actions.cancel') }}</button>

                <primary-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" class="ml-3 ">
                    {{ $t("actions.save") }}
                </primary-button>

            </div>
        </template>

    </jet-form-section>

</template>


<script>
import { defineComponent } from "vue";
import { rooms } from "@/Shared/rooms";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetFormSection from "@/Jetstream/FormSection";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import PageTitle from "@/Shared/Components/PageTitle";
import { useForm } from "@inertiajs/inertia-vue3";
import FormCard from "@/Shared/Components/FormCard";
import RequiredMark from "@/Shared/Components/RequiredMark";

export default defineComponent({
    props: ['project'],
    inheritAttrs: false,
    layout: MainLayout,
    emits: ["closeModal"],
    components: {
        PageTitle,
        PrimaryButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        FormCard,
        RequiredMark,
    },
    methods: {
        cancel() {
            this.$emit("closeModal");
        },
        createApartment() {
            this.form.rooms = this.form.rooms.value;
            this.form.post(this.route("advisors.apartments.store", this.project.uuid), {
                preserveScroll: true,
                onSuccess: () => this.$notify({
                    group: "success",
                    title: 'actions.success',
                    text: 'apartments.created'
                })
            });
        },
    },
    setup(props) {

        const form = useForm({
            number: null,
            floor: null,
            rooms: 0,
            area: 0,
            parking: null,
            base_fee: props.project.base_fee,
            consultancy_fee: props.project.consultancy_fee,
            building: null,
        });

        return { form, rooms };
    },
});
</script>

<style scoped></style>
